import { useState } from 'react';

interface RoleSelectorProps {
  currentRole: string;
  onRoleChange: (newRole: string) => Promise<{success: boolean, error?: string}>;
  disabled?: boolean;
}

const ROLES = [
  { value: 'user', label: 'User', description: 'Standard user with basic permissions' },
  { value: 'admin', label: 'Admin', description: 'Administrator with full access' },
  { value: 'csr', label: 'CSR', description: 'Customer Service Representative' }
];

export default function RoleSelector({ currentRole, onRoleChange, disabled }: RoleSelectorProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRoleChange = async (newRole: string) => {
    if (newRole === currentRole) return;
    
    setIsUpdating(true);
    setError(null);
    
    try {
      const result = await onRoleChange(newRole);
      if (!result.success) {
        setError(result.error || 'Failed to update role');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-2">
      <select
        value={currentRole}
        onChange={(e) => handleRoleChange(e.target.value)}
        disabled={disabled || isUpdating}
        className={`
          px-3 py-2 border rounded-md text-sm min-w-[120px]
          ${disabled || isUpdating 
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
            : 'bg-white text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:outline-none'
          }
          ${error ? 'border-red-300' : 'border-gray-300'}
        `}
      >
        {ROLES.map(role => (
          <option key={role.value} value={role.value}>
            {role.label}
          </option>
        ))}
      </select>
      
      {isUpdating && (
        <div className="flex items-center space-x-2 text-blue-600 text-sm">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span>Updating...</span>
        </div>
      )}
      
      {error && (
        <div className="text-red-600 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
